import React from 'react';
import BriefSectionWrapper from '@99designs/apps/spa/pages/create/components/BriefSectionWrapper';
import ContentSetting from '@99designs/apps/spa/pages/create/components/ContentSetting';
import {
    getContestTierColor,
    getContestTierName,
    useLogoPackageConfig,
    useWebPackageConfig,
} from '@99designs/brief';
import { Box, Paragraph, SectionHeading, Stack } from '@99designs/emu';
import {
    LogoContestTierChoiceField,
    SquarespacePackageChoiceField,
} from '@99designs/graph-utils/types';
import { PackageCard as ContestPackageCard, FeaturesList } from '@99designs/marketing-ui';
import { PackageCard } from '@99designs/spa-common';
import PaymentSummary from '../../components/PaymentSummary';
import { StructuredBriefFragment } from '../DynamicBrief/DynamicBrief.generated';
import { Layout, MainContent, SideContent } from './Layout';

interface PaymenPageProps {
    brief: StructuredBriefFragment;
}

function useLogoPackage(brief: StructuredBriefFragment) {
    const { logoPackages, logoPackagesError } = useLogoPackageConfig();

    if (logoPackagesError) {
        throw logoPackagesError;
    }

    const logoPackageId = (
        brief.dynamicFields.find(
            (f) => f.__typename === 'LogoContestTierChoiceField'
        ) as LogoContestTierChoiceField
    ).logoContestTierChoiceValue;

    return logoPackages.find((item) => item.id === logoPackageId);
}

function useWebPackage(brief: StructuredBriefFragment) {
    const { webPackages, webPackagesError } = useWebPackageConfig();

    if (webPackagesError) {
        throw webPackagesError;
    }

    const webPackageId = (
        brief.dynamicFields.find(
            (f) => f.__typename === 'SquarespacePackageChoiceField'
        ) as SquarespacePackageChoiceField
    ).squarespacePackageChoiceValue;

    return webPackages.find((item) => item.id === webPackageId);
}

function PaymentPage({ brief }: PaymenPageProps) {
    const logoPackage = useLogoPackage(brief);
    const webPackage = useWebPackage(brief);

    return (
        <ContentSetting>
            <Layout>
                <MainContent>
                    <Stack space="1.5bu">
                        <Box height="2bu">
                            <SectionHeading>{__("What's included")}</SectionHeading>
                        </Box>
                        {logoPackage && (
                            <ContestPackageCard
                                id={logoPackage.id}
                                title={getContestTierName(logoPackage.tier) || logoPackage.id}
                                price={{
                                    currency: logoPackage.pricing.currency,
                                    amountInCents: logoPackage.pricing.amountInCents,
                                }}
                                featureColor={getContestTierColor(logoPackage.tier)}
                                isSelected={true}
                                tier={logoPackage.tier}
                                headingAccessibilityLevel={4}
                            >
                                <Stack space="0.5bu">
                                    <Paragraph>{logoPackage.description}</Paragraph>
                                    <FeaturesList features={logoPackage.details} />
                                </Stack>
                            </ContestPackageCard>
                        )}
                        {webPackage && (
                            <PackageCard
                                id={webPackage.id}
                                title={webPackage.title}
                                description={webPackage.description}
                                price={webPackage.price}
                                content={webPackage.content}
                                logo={webPackage.logo}
                                featureColor={webPackage.featureColor}
                                isSelected={true}
                                isSummaryCard={true}
                                features={webPackage.features}
                            />
                        )}
                    </Stack>
                </MainContent>
                <SideContent offset={'50px'}>
                    <BriefSectionWrapper>
                        <PaymentSummary
                            taxLabel={brief.gstApplicable ? 'GST' : __('Tax')}
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            {...brief.paymentSummary!}
                        />
                    </BriefSectionWrapper>
                </SideContent>
            </Layout>
        </ContentSetting>
    );
}

export default PaymentPage;
